import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
	SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	SmartField_Api_Contracts_ExperimentalLocation_SiteTreatmentDto,
	SmartField_Api_Contracts_Sensor_DimSensorDto,
	SmartField_Api_Contracts_Site_SiteDto,
} from "../../api";
import {
	treatmentsUpdateTreatment,
	siteGeometryImportGeoJson,
	siteGeometryExportGeoJsonBundle,
} from "../../api";
import { MULTI_CHANNEL_DEVICES } from "./sensorConstants";
import { Modal } from "../../components/ui/Modal";

const SENSOR_TYPE_LABELS: Record<string, string> = {
	AirTemperature: "Air Temp",
	RelativeAirHumidity: "RH",
	AirPressure: "Air Pressure",
	SoilTemperature: "Soil Temp",
	SoilMoisture: "Soil Moisture",
	VolumetricWaterContent: "VWC",
	MatricPotential: "Matric Pot.",
	SoilAbsolutePressure: "Soil Abs. Pressure",
	SaturationExtractEC: "Sat. Extract EC",
	NeutronCount: "Neutron Count",
	NitricOxide: "NO",
	NitrogenDioxide: "NO₂",
	CarbonDioxide: "CO₂",
	NitrousOxide: "N₂O",
	Ammonia: "NH₃",
	WaterVapor: "H₂O",
	Ozone: "O₃",
	Methane: "CH₄",
	Ethane: "C₂H₆",
	N2OIsotopologue446: "N₂O 446",
	N2OIsotopologue448: "N₂O 448",
	N2OIsotopomer456: "N₂O 456",
	N2OIsotopomer546: "N₂O 546",
	Precipitation: "Precip.",
	WindSpeed: "Wind Speed",
	WindDirection: "Wind Dir.",
	WindVector3D: "Wind 3D",
	WindGust: "Wind Gust",
	SolarRadiation: "Solar Rad.",
	NetRadiation: "Net Rad.",
	IncidentShortWave: "SW In",
	ReflectedShortWave: "SW Out",
	EmittedLongWave: "LW Out",
	IncidentLongWave: "LW In",
	SonicTemperature: "Sonic Temp.",
	VaporPressure: "Vapor Pressure",
	VaporPressureDeficit: "VPD",
	CropTargetHeight: "Crop Target H.",
	CropMaxHeight: "Crop Max H.",
	CropMinHeight: "Crop Min H.",
	CropMeanHeight: "Crop Mean H.",
	CropHeightStdDev: "Crop H. StdDev",
	XAxisLevel: "X Level",
	YAxisLevel: "Y Level",
};

function toDateInputValue(date?: string | null): string {
	if (!date) {
		return "";
	}
	return date.slice(0, 10);
}

function humanizeSensorType(type?: string | null): string {
	if (!type) {
		return "";
	}
	return SENSOR_TYPE_LABELS[type] ?? type;
}

type Props = {
	siteId: number;
	asOf: string;
	site: SmartField_Api_Contracts_Site_SiteDto | null;
	plots: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	fields: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	blocks: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	miniBlocks: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto[];
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[];
	siteTreatments: SmartField_Api_Contracts_ExperimentalLocation_SiteTreatmentDto[];
	elsLoading?: boolean;
	sensorsLoading?: boolean;
	visibleLayers: Set<string>;
	onToggleLayer: (layer: string) => void;
	canEdit: boolean;
	onEditEl: (
		el: SmartField_Api_Contracts_ExperimentalLocation_ExperimentalLocationDto,
	) => void;
	onDeleteEl: (elId: number) => void;
	onPlaceChannel: (
		channelSensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
	) => void;
	onFocusEl: (elId: number) => void;
	onFocusChannel: (channelKey: string) => void;
};

function InlineSpinner() {
	return (
		<svg className="inline-block h-3.5 w-3.5 animate-spin text-gray-400 ml-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
			<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
		</svg>
	);
}

function LayerRow({
	label,
	visible,
	onToggle,
	children,
}: {
	label: string;
	layerKey: string;
	visible: boolean;
	onToggle: () => void;
	children?: React.ReactNode;
}) {
	return (
		<div>
			<div className="flex items-center justify-between">
				<label className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer select-none">
					<input
						type="checkbox"
						checked={visible}
						onChange={onToggle}
						className="rounded border-gray-300 text-smartfield focus:ring-smartfield"
					/>
					{label}
				</label>
			</div>
			{children}
		</div>
	);
}

function SectionHeader({
	label,
	count,
	open,
	onToggle,
	loading = false,
}: {
	label: string;
	count: number;
	open: boolean;
	onToggle: () => void;
	loading?: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onToggle}
			className="flex items-center gap-1.5 w-full text-left mt-2"
		>
			<svg
				className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${open ? "rotate-90" : ""}`}
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M9 5l7 7-7 7"
				/>
			</svg>
			<span className="text-xs font-medium text-gray-500">{label}</span>
			{loading ? (
				<InlineSpinner />
			) : !open && (
				<span className="ml-auto text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">
					{count}
				</span>
			)}
		</button>
	);
}

function EditDeleteButtons({
	onEdit,
	onDelete,
}: {
	onEdit: () => void;
	onDelete: () => void;
}) {
	return (
		<div className="flex gap-1 flex-shrink-0">
			<button
				onClick={onEdit}
				className="p-1 text-gray-400 hover:text-smartfield"
				title="Edit"
			>
				<svg
					className="w-3.5 h-3.5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
					/>
				</svg>
			</button>
			<button
				onClick={onDelete}
				className="p-1 text-gray-400 hover:text-red-500"
				title="Delete"
			>
				<svg
					className="w-3.5 h-3.5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
					/>
				</svg>
			</button>
		</div>
	);
}

type DeviceChannelGroups = Record<
	string,
	Record<string, SmartField_Api_Contracts_Sensor_DimSensorDto[]>
>;

function groupByDeviceAndChannel(
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
): DeviceChannelGroups {
	const groups: DeviceChannelGroups = {};
	for (const s of sensors) {
		const deviceKey = s.deviceSerialKey ?? "unknown";
		const friendlyName = s.deviceFriendlyName ?? "";
		const channelKey = MULTI_CHANNEL_DEVICES.has(friendlyName)
			? "all"
			: String(s.channelNumber ?? 0);
		if (!groups[deviceKey]) {
			groups[deviceKey] = {};
		}
		if (!groups[deviceKey][channelKey]) {
			groups[deviceKey][channelKey] = [];
		}
		groups[deviceKey][channelKey].push(s);
	}
	return groups;
}

function channelLabel(
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
): string {
	const first = sensors[0];
	if (MULTI_CHANNEL_DEVICES.has(first?.deviceFriendlyName ?? "")) {
		const channels = [
			...new Set(
				sensors
					.map((s) => s.channelNumber)
					.filter((n): n is number => n != null),
			),
		].sort((a, b) => a - b);
		return channels.length > 1
			? `All depths (Channel ${channels[0]}–${channels[channels.length - 1]})`
			: `Channel ${channels[0] ?? 1}`;
	}
	if (first?.friendlyName) {
		return first.friendlyName;
	}
	const ch = first?.channelNumber;
	return ch != null && ch > 0 ? `Channel ${ch}` : "Channel 0";
}

function deviceLabel(
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
): string {
	const first = sensors[0];
	return (
		first?.deviceFriendlyName ?? first?.deviceSerialKey ?? "Unknown device"
	);
}

function channelMatchesSearch(
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
	q: string,
): boolean {
	if (!q) {
		return true;
	}
	const lower = q.toLowerCase();
	const first = sensors[0];
	return [
		first?.friendlyName,
		first?.deviceFriendlyName,
		first?.manufacturer,
		first?.deviceSerialKey,
		...sensors.map((s) => s.measureName),
		...sensors.map((s) =>
			s.measureName ? SENSOR_TYPE_LABELS[s.measureName] : null,
		),
	].some((v) => v?.toLowerCase().includes(lower));
}

function channelDragData(
	sensors: SmartField_Api_Contracts_Sensor_DimSensorDto[],
): string {
	return sensors.map((s) => String(s.id)).join(",");
}

export function LayerSidebar({
	siteId,
	asOf,
	site,
	plots,
	fields,
	blocks,
	miniBlocks,
	sensors,
	siteTreatments,
	elsLoading = false,
	sensorsLoading = false,
	visibleLayers,
	onToggleLayer,
	canEdit,
	onEditEl,
	onDeleteEl,
	onPlaceChannel,
	onFocusEl,
	onFocusChannel,
}: Props) {
	const queryClient = useQueryClient();

	const updateTreatmentMutation = useMutation({
		mutationFn: ({
			treatmentId,
			friendlyName,
			validFrom,
			validTo,
		}: {
			treatmentId: number;
			friendlyName: string;
			validFrom?: string | null;
			validTo?: string | null;
		}) =>
			treatmentsUpdateTreatment({
				siteId,
				treatmentId,
				requestBody: {
					friendlyName: friendlyName || null,
					validFrom: validFrom || null,
					validTo: validTo || null,
				},
			}),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["site-treatments", siteId] });
			toast.success("Treatment saved.");
		},
		onError: () => {
			toast.error("Failed to save treatment.");
		},
	});

	type TreatmentEdit = { friendlyName: string; validFrom: string; validTo: string; plotLocationIds: number[] };
	const [treatmentEdits, setTreatmentEdits] = useState<Record<number, TreatmentEdit>>({});
	const [treatmentsOpen, setTreatmentsOpen] = useState(false);
	const [editingTreatmentId, setEditingTreatmentId] = useState<number | null>(null);
	const now = new Date(asOf);
	const knownSensors = sensors.filter(
		(s) => s.id !== -1 && s.measureName != null,
	);
	const placedSensors = knownSensors.filter(
		(s) =>
			s.currentExperimentalLocationId &&
			(!s.currentValidTo || new Date(s.currentValidTo) > now),
	);
	const unplacedSensors = knownSensors.filter(
		(s) =>
			!s.currentExperimentalLocationId ||
			(s.currentValidTo != null && new Date(s.currentValidTo) <= now),
	);

	const placedGroups = groupByDeviceAndChannel(placedSensors);
	const unplacedGroups = groupByDeviceAndChannel(unplacedSensors);

	const placedChannelCount = Object.values(placedGroups).reduce(
		(sum, channels) => sum + Object.keys(channels).length,
		0,
	);
	const unplacedChannelCount = Object.values(unplacedGroups).reduce(
		(sum, channels) => sum + Object.keys(channels).length,
		0,
	);

	const sortedFields = [...fields].sort(
		(a, b) => (a.fieldId ?? 0) - (b.fieldId ?? 0),
	);
	const sortedPlots = [...plots].sort(
		(a, b) =>
			(a.fieldId ?? 0) - (b.fieldId ?? 0) || (a.plotId ?? 0) - (b.plotId ?? 0),
	);

	const [fieldsOpen, setFieldsOpen] = useState(false);
	const [plotsOpen, setPlotsOpen] = useState(false);
	const [blocksOpen, setBlocksOpen] = useState(false);
	const [miniBlocksOpen, setMiniBlocksOpen] = useState(false);
	const [sensorsOpen, setSensorsOpen] = useState(false);
	const [unplacedOpen, setUnplacedOpen] = useState(false);

	const [searchQuery, setSearchQuery] = useState("");
	const [placedSearchQuery, setPlacedSearchQuery] = useState("");
	const [openDevices, setOpenDevices] = useState<Set<string>>(new Set());

	const [importPending, setImportPending] = useState(false);
	const [importPreview, setImportPreview] = useState<{ updated: number; elsUpdated: number; skipped: number; created: number; treatmentsLinked: number; errors: string[] } | null>(null);
	const [pendingFile, setPendingFile] = useState<File | null>(null);
	const [importOptions, setImportOptions] = useState({ createMissing: false, validFrom: "", validTo: "" });
	const importInputRef = useRef<HTMLInputElement>(null);

	async function handleExport() {
		try {
			const blob = await siteGeometryExportGeoJsonBundle({ siteId, asOf });
			const url = URL.createObjectURL(blob as Blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${site?.serialNumber ?? siteId}_geometry.zip`;
			a.click();
			URL.revokeObjectURL(url);
		} catch {
			toast.error("Export failed");
		}
	}

	function importGeoJson(file: File, dryRun: boolean, options: typeof importOptions) {
		return siteGeometryImportGeoJson({
			siteId,
			dryRun,
			createMissing: options.createMissing,
			validFrom: options.validFrom || undefined,
			validTo: options.validTo || undefined,
			formData: { file },
		});
	}

	async function handleImportFileSelected(file: File, options = importOptions) {
		setPendingFile(file);
		setImportOptions(options);
		setImportPending(true);
		try {
			const data = await importGeoJson(file, true, options);
			setImportPreview({
				updated: data.updated ?? 0,
				elsUpdated: data.eLsUpdated ?? 0,
				skipped: data.skipped ?? 0,
				created: data.created ?? 0,
				treatmentsLinked: data.treatmentsLinked ?? 0,
				errors: data.errors ?? [],
			});
		} catch (err: unknown) {
			const detail = err instanceof Error ? err.message : "Import validation failed";
			toast.error(detail);
			setPendingFile(null);
			setImportPreview(null);
		} finally {
			setImportPending(false);
			if (importInputRef.current) {
				importInputRef.current.value = "";
			}
		}
	}

	async function handleImportConfirm() {
		if (!pendingFile) {
			return;
		}
		setImportPending(true);
		try {
			const data = await importGeoJson(pendingFile, false, importOptions);
			toast.success(`Created ${data.created ?? 0}, updated ${data.updated ?? 0} location(s) (${data.eLsUpdated ?? 0} EL rows)`);
			void queryClient.invalidateQueries({ queryKey: ["experimental-locations", siteId] });
			void queryClient.invalidateQueries({ queryKey: ["site-treatments", siteId] });
		} catch (err: unknown) {
			const detail = err instanceof Error ? err.message : "Import failed";
			toast.error(detail);
		} finally {
			setImportPending(false);
			setImportPreview(null);
			setPendingFile(null);
		}
	}

	function toggleDevice(deviceKey: string) {
		setOpenDevices((prev) => {
			const next = new Set(prev);
			if (next.has(deviceKey)) {
				next.delete(deviceKey);
			} else {
				next.add(deviceKey);
			}
			return next;
		});
	}

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-4 space-y-5 text-sm">
			{/* GeoJSON Export / Import */}
			<div className="flex gap-2">
				<button
					type="button"
					onClick={() => void handleExport()}
					className="flex-1 px-3 py-1.5 text-xs bg-gray-50 text-gray-700 border border-gray-200 rounded hover:bg-gray-100"
				>
					Export GeoJSON
				</button>
				{canEdit && (
					<>
						<button
							type="button"
							disabled={importPending}
							onClick={() => importInputRef.current?.click()}
							className="flex-1 px-3 py-1.5 text-xs bg-gray-50 text-gray-700 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50"
						>
							Import GeoJSON
						</button>
						<input
							ref={importInputRef}
							type="file"
							accept=".geojson,.json"
							className="hidden"
							onChange={(e) => {
								const f = e.target.files?.[0];
								if (f) {
									void handleImportFileSelected(f);
								}
							}}
						/>
					</>
				)}
			</div>

			{/* Import preview confirmation */}
			{importPreview && (
				<div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2 text-xs">
					<p className="font-semibold text-blue-800">Import preview</p>
					<label className="flex items-center gap-1.5 text-blue-800">
						<input
							type="checkbox"
							checked={importOptions.createMissing}
							disabled={importPending}
							onChange={(e) => pendingFile && void handleImportFileSelected(pendingFile, { ...importOptions, createMissing: e.target.checked })}
						/>
						Create missing fields, blocks and plots
					</label>
					{importOptions.createMissing && (
						<div className="flex gap-2 text-blue-800">
							{(["validFrom", "validTo"] as const).map((key) => (
								<label key={key} className="flex-1">
									{key === "validFrom" ? "Valid from" : "Valid to"}
									<input
										type="date"
										value={importOptions[key]}
										disabled={importPending}
										onChange={(e) => setImportOptions({ ...importOptions, [key]: e.target.value })}
										onBlur={() => pendingFile && void handleImportFileSelected(pendingFile)}
										className="w-full border border-gray-300 rounded px-1 py-0.5"
									/>
								</label>
							))}
						</div>
					)}
					<p className="text-blue-700">
						Will update <span className="font-semibold">{importPreview.updated}</span> location(s)
						{" "}(<span className="font-semibold">{importPreview.elsUpdated}</span> EL rows).
						{importPreview.created > 0 && ` Will create ${importPreview.created} location(s).`}
						{importPreview.treatmentsLinked > 0 && ` Will link ${importPreview.treatmentsLinked} treatment(s).`}
						{importPreview.skipped > 0 && ` ${importPreview.skipped} unchanged or without geometry.`}
					</p>
					{importPreview.errors.length > 0 && (
						<ul className="text-red-700 space-y-0.5 list-disc list-inside">
							{importPreview.errors.map((e, i) => <li key={i}>{e}</li>)}
						</ul>
					)}
					<div className="flex gap-2 pt-1">
						<button
							type="button"
							disabled={importPending}
							onClick={() => { setImportPreview(null); setPendingFile(null); }}
							className="flex-1 px-2 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
						>
							Cancel
						</button>
						<button
							type="button"
							disabled={importPending || importPreview.errors.length > 0}
							onClick={() => void handleImportConfirm()}
							className="flex-1 px-2 py-1 bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
						>
							Confirm
						</button>
					</div>
				</div>
			)}

			{/* Boundary */}
			<LayerRow
				label="Site Boundary"
				layerKey="boundary"
				visible={visibleLayers.has("boundary")}
				onToggle={() => onToggleLayer("boundary")}
			>
			</LayerRow>

			{/* Fields */}
			<LayerRow
				label="Fields"
				layerKey="fields"
				visible={visibleLayers.has("fields")}
				onToggle={() => onToggleLayer("fields")}
			>
				<SectionHeader
					label={elsLoading ? "Fields" : `${sortedFields.length} field${sortedFields.length !== 1 ? "s" : ""}`}
					count={sortedFields.length}
					open={fieldsOpen}
					onToggle={() => setFieldsOpen((v) => !v)}
					loading={elsLoading}
				/>
				{fieldsOpen && (
					<>
								<div className="mt-2 space-y-1">
							{sortedFields.map((field) => (
								<div
									key={field.id}
									className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50"
								>
									<button
										type="button"
										onClick={() => field.id != null && onFocusEl(field.id)}
										className="text-gray-700 truncate text-left hover:text-smartfield text-xs"
									>
										{field.friendlyName ??
											field.entityIdentifier ??
											`Field ${field.fieldId}`}
									</button>
									{canEdit && (
										<EditDeleteButtons
											onEdit={() => onEditEl(field)}
											onDelete={() => field.id != null && onDeleteEl(field.id)}
										/>
									)}
								</div>
							))}
							{sortedFields.length === 0 && (
								<p className="text-xs text-gray-400 px-2">No fields yet</p>
							)}
						</div>
					</>
				)}
			</LayerRow>

			{/* Blocks */}
			<LayerRow
				label="Blocks"
				layerKey="blocks"
				visible={visibleLayers.has("blocks")}
				onToggle={() => onToggleLayer("blocks")}
			>
				<SectionHeader
					label={elsLoading ? "Blocks" : `${blocks.length} block${blocks.length !== 1 ? "s" : ""}`}
					count={blocks.length}
					open={blocksOpen}
					onToggle={() => setBlocksOpen((v) => !v)}
					loading={elsLoading}
				/>
				{blocksOpen && (
					<div className="mt-2 space-y-1">
						{blocks.map((block) => (
							<div
								key={block.id}
								className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50"
							>
								<button
									type="button"
									onClick={() => block.id != null && onFocusEl(block.id)}
									className="text-gray-700 truncate text-left hover:text-smartfield text-xs"
								>
									{block.friendlyName ?? block.entityIdentifier ?? `Block ${block.blockId}`}
								</button>
								{canEdit && (
									<EditDeleteButtons
										onEdit={() => onEditEl(block)}
										onDelete={() => block.id != null && onDeleteEl(block.id)}
									/>
								)}
							</div>
						))}
						{blocks.length === 0 && (
							<p className="text-xs text-gray-400 px-2">No blocks yet</p>
						)}
					</div>
				)}
			</LayerRow>

			{/* Mini-blocks */}
			<LayerRow
				label="Mini-blocks"
				layerKey="miniblocks"
				visible={visibleLayers.has("miniblocks")}
				onToggle={() => onToggleLayer("miniblocks")}
			>
				<SectionHeader
					label={elsLoading ? "Mini-blocks" : `${miniBlocks.length} mini-block${miniBlocks.length !== 1 ? "s" : ""}`}
					count={miniBlocks.length}
					open={miniBlocksOpen}
					onToggle={() => setMiniBlocksOpen((v) => !v)}
					loading={elsLoading}
				/>
				{miniBlocksOpen && (
					<div className="mt-2 space-y-1">
						{miniBlocks.map((mb) => (
							<div
								key={mb.id}
								className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50"
							>
								<button
									type="button"
									onClick={() => mb.id != null && onFocusEl(mb.id)}
									className="text-gray-700 truncate text-left hover:text-smartfield text-xs"
								>
									{mb.friendlyName ?? mb.entityIdentifier ?? `Mini-block ${mb.miniBlockId}`}
								</button>
								{canEdit && (
									<EditDeleteButtons
										onEdit={() => onEditEl(mb)}
										onDelete={() => mb.id != null && onDeleteEl(mb.id)}
									/>
								)}
							</div>
						))}
						{miniBlocks.length === 0 && (
							<p className="text-xs text-gray-400 px-2">No mini-blocks yet</p>
						)}
					</div>
				)}
			</LayerRow>

			{/* Plots */}
			<LayerRow
				label="Plots"
				layerKey="plots"
				visible={visibleLayers.has("plots")}
				onToggle={() => onToggleLayer("plots")}
			>
				<SectionHeader
					label={elsLoading ? "Plots" : `${sortedPlots.length} plot${sortedPlots.length !== 1 ? "s" : ""}`}
					count={sortedPlots.length}
					open={plotsOpen}
					onToggle={() => setPlotsOpen((v) => !v)}
					loading={elsLoading}
				/>
				{plotsOpen && (
					<>
							<div className="mt-2 space-y-1">
							{sortedPlots.map((plot) => (
								<div
									key={plot.id}
									className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50"
								>
									<button
										type="button"
										onClick={() => plot.id != null && onFocusEl(plot.id)}
										className="text-gray-700 truncate text-left hover:text-smartfield text-xs"
									>
										{plot.friendlyName ??
											plot.entityIdentifier ??
											`Plot ${plot.plotId}`}
									</button>
									{canEdit && (
										<EditDeleteButtons
											onEdit={() => onEditEl(plot)}
											onDelete={() => plot.id != null && onDeleteEl(plot.id)}
										/>
									)}
								</div>
							))}
							{sortedPlots.length === 0 && (
								<p className="text-xs text-gray-400 px-2">No plots yet</p>
							)}
						</div>
					</>
				)}
			</LayerRow>

			{/* Treatments */}
			{siteTreatments && siteTreatments.length > 0 && (
				<div>
					<div className="flex items-center justify-between">
						<span className="text-sm font-semibold text-gray-700">Treatments</span>
					</div>
					<SectionHeader
						label={`${siteTreatments.length} treatment${siteTreatments.length !== 1 ? "s" : ""}`}
						count={siteTreatments.length}
						open={treatmentsOpen}
						onToggle={() => setTreatmentsOpen((v) => !v)}
					/>
					{treatmentsOpen && (
						<div className="mt-2 space-y-1">
							{siteTreatments.map((t) => {
								const tid = t.id!;
								const plotCount = t.plotLocationIds?.length ?? 0;
								return (
									<div
										key={tid}
										className="flex items-center justify-between px-2 py-1 rounded bg-gray-50 border border-gray-100"
									>
										<div className="min-w-0">
											<p className="text-xs text-gray-800 truncate">
												<span className="font-semibold text-gray-500">
													T{t.treatmentNumber}·F{t.factor}
												</span>{" "}
												{t.friendlyName || `Treatment ${t.treatmentNumber}`}
											</p>
											<p className="text-xs text-gray-400">
												{toDateInputValue(t.validFrom) || "..."} to {toDateInputValue(t.validTo) || "..."}
												{" - "}
												{plotCount} plot{plotCount !== 1 ? "s" : ""}
											</p>
										</div>
										{canEdit && (
											<button
												type="button"
												onClick={() => {
													setTreatmentEdits((prev) => ({
														...prev,
														[tid]: {
															friendlyName: t.friendlyName ?? "",
															validFrom: toDateInputValue(t.validFrom),
															validTo: toDateInputValue(t.validTo),
															plotLocationIds: t.plotLocationIds ?? [],
														},
													}));
													setEditingTreatmentId(tid);
												}}
												className="p-1 text-gray-400 hover:text-smartfield shrink-0"
												title="Edit"
												aria-label="Edit treatment"
											>
												<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
													/>
												</svg>
											</button>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			)}

			<Modal open={editingTreatmentId != null} onClose={() => setEditingTreatmentId(null)} title="Edit treatment">
				{editingTreatmentId != null && (() => {
					const t = siteTreatments.find((st) => st.id === editingTreatmentId);
					if (!t) return null;
					const tid = editingTreatmentId;
					const edit = treatmentEdits[tid] ?? {
						friendlyName: t.friendlyName ?? "",
						validFrom: toDateInputValue(t.validFrom),
						validTo: toDateInputValue(t.validTo),
						plotLocationIds: t.plotLocationIds ?? [],
					};
					const saving = updateTreatmentMutation.isPending;
					const handleSave = async () => {
						try {
							await updateTreatmentMutation.mutateAsync({
								treatmentId: tid,
								friendlyName: edit.friendlyName,
								validFrom: edit.validFrom || null,
								validTo: edit.validTo || null,
							});
							setEditingTreatmentId(null);
						} catch {
							// keep modal open; onError handlers already show a toast
						}
					};
					return (
						<div className="p-4 space-y-3">
							<h2 className="text-sm font-semibold text-gray-800">
								T{t.treatmentNumber}·F{t.factor}
							</h2>
							<label className="block text-xs text-gray-500">
								Name
								<input
									type="text"
									value={edit.friendlyName}
									onChange={(e) =>
										setTreatmentEdits((prev) => ({
											...prev,
											[tid]: { ...edit, friendlyName: e.target.value },
										}))
									}
									placeholder={`Treatment ${t.treatmentNumber}`}
									className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
								/>
							</label>
							<div className="flex gap-3">
								<label className="flex-1 block text-xs text-gray-500">
									From
									<input
										type="date"
										value={edit.validFrom}
										onChange={(e) =>
											setTreatmentEdits((prev) => ({
												...prev,
												[tid]: { ...edit, validFrom: e.target.value },
											}))
										}
										className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
									/>
								</label>
								<label className="flex-1 block text-xs text-gray-500">
									To
									<input
										type="date"
										value={edit.validTo}
										onChange={(e) =>
											setTreatmentEdits((prev) => ({
												...prev,
												[tid]: { ...edit, validTo: e.target.value },
											}))
										}
										className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
									/>
								</label>
							</div>
							<div className="flex justify-end gap-2 pt-2">
								<button
									type="button"
									onClick={() => setEditingTreatmentId(null)}
									className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
								>
									Cancel
								</button>
								<button
									type="button"
									disabled={saving}
									onClick={handleSave}
									className="px-3 py-1.5 text-sm bg-smartfield text-white rounded hover:bg-smartfield/90 disabled:opacity-50"
								>
									Save
								</button>
							</div>
						</div>
					);
				})()}
			</Modal>

			{/* Placed sensors */}
			<LayerRow
				label="Sensors"
				layerKey="sensors"
				visible={visibleLayers.has("sensors")}
				onToggle={() => onToggleLayer("sensors")}
			>
				<SectionHeader
					label={sensorsLoading ? "Sensors" : `${placedChannelCount} placed channel${placedChannelCount !== 1 ? "s" : ""}`}
					count={placedChannelCount}
					open={sensorsOpen}
					onToggle={() => setSensorsOpen((v) => !v)}
					loading={sensorsLoading}
				/>
				{sensorsOpen && (
					<div className="mt-2 space-y-3">
						<input
							type="text"
							value={placedSearchQuery}
							onChange={(e) => setPlacedSearchQuery(e.target.value)}
							placeholder="Search sensors…"
							className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
						/>
						{Object.entries(placedGroups).map(([deviceKey, channelMap]) => {
							const visibleChannels = Object.entries(channelMap).filter(
								([, ch]) => channelMatchesSearch(ch, placedSearchQuery),
							);
							if (visibleChannels.length === 0) return null;
							const firstChannel = Object.values(channelMap)[0];
							const rep = firstChannel?.[0];
							const isOpen = openDevices.has(deviceKey);
							return (
								<div key={deviceKey}>
									<button
										type="button"
										onClick={() => toggleDevice(deviceKey)}
										className="flex items-center gap-1.5 w-full text-left px-2 py-1 rounded hover:bg-gray-50"
									>
										<svg
											className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-90" : ""}`}
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
										</svg>
										<span className="text-xs font-semibold text-gray-700 truncate flex-1">
											{rep?.deviceFriendlyName ?? deviceKey}
											{rep?.deviceFriendlyName && (
												<span className="ml-1 font-normal text-gray-400">· {deviceKey}</span>
											)}
										</span>
										{!isOpen && (
											<span className="ml-auto text-xs bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5 flex-shrink-0">
												{visibleChannels.length}
											</span>
										)}
									</button>
									{isOpen && (
										<>
											{rep?.manufacturer && (
												<p className="text-xs text-gray-400 px-2">{rep.manufacturer}</p>
											)}
											{visibleChannels.map(([chKey, channelSensors]) => {
												const representative = channelSensors[0];
												if (!representative) return null;
												const scope = representative.currentScope;
												const plotNumber = representative.currentPlotNumber;
												const chNum = representative.channelNumber;
												const isMultiChannel = MULTI_CHANNEL_DEVICES.has(
													representative.deviceFriendlyName ?? "",
												);
												const label = channelLabel(channelSensors);
												const showChNum =
													!isMultiChannel &&
													chNum != null &&
													label !== `Channel ${chNum}`;
												return (
													<div
														key={chKey}
														className="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50"
														draggable={canEdit}
														onDragStart={(e) =>
															e.dataTransfer.setData(
																"channel-sensor-ids",
																channelDragData(channelSensors),
															)
														}
													>
														<button
															type="button"
															className="min-w-0 flex-1 text-left"
															onClick={() => onFocusChannel(`${deviceKey}|${chKey}`)}
															title="Show on map"
														>
															<p className="text-xs text-gray-800 truncate">
																{label}
																{showChNum && (
																	<span className="ml-1 text-gray-400">
																		· Channel {chNum}
																	</span>
																)}
															</p>
															<p className="text-xs text-gray-500">
																{channelSensors
																	.map((s) => humanizeSensorType(s.measureName))
																	.join(" · ")}
															</p>
															{representative.currentValidFrom && (
																<p className="text-xs text-gray-400">
																	from{" "}
																	{new Date(
																		representative.currentValidFrom,
																	).toLocaleDateString()}
																</p>
															)}
														</button>
														<div className="flex items-center gap-1 flex-shrink-0">
															{scope && (
																<span className="text-xs bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">
																	{scope === "Plot" && plotNumber != null
																		? `P${plotNumber}`
																		: scope}
																</span>
															)}
															{canEdit && (
																<button
																	type="button"
																	onClick={() => onPlaceChannel(channelSensors)}
																	className="p-1 text-gray-400 hover:text-smartfield"
																	title="Edit placement"
																	aria-label="Edit placement"
																>
																	<svg
																		aria-hidden="true"
																		className="w-3.5 h-3.5"
																		fill="none"
																		stroke="currentColor"
																		viewBox="0 0 24 24"
																	>
																		<path
																			strokeLinecap="round"
																			strokeLinejoin="round"
																			strokeWidth={2}
																			d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
																		/>
																	</svg>
																</button>
															)}
														</div>
													</div>
												);
											})}
										</>
									)}
								</div>
							);
						})}
						{placedChannelCount === 0 && (
							<p className="text-xs text-gray-400 px-2">No placed sensors</p>
						)}
						{placedChannelCount > 0 && placedSearchQuery && Object.values(placedGroups).every((channelMap) =>
							Object.values(channelMap).every((ch) => !channelMatchesSearch(ch, placedSearchQuery)),
						) && (
							<p className="text-xs text-gray-400 px-2">No results for "{placedSearchQuery}"</p>
						)}
					</div>
				)}
			</LayerRow>

			{/* Unplaced sensors */}
			{unplacedChannelCount > 0 && (
				<div>
					<SectionHeader
						label={`${unplacedChannelCount} unplaced channel${unplacedChannelCount !== 1 ? "s" : ""}`}
						count={unplacedChannelCount}
						open={unplacedOpen}
						onToggle={() => setUnplacedOpen((v) => !v)}
					/>
					{unplacedOpen && (
					<>
					<input
						type="text"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder="Search sensors…"
						className="w-full mt-2 mb-2 px-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
					/>
					<div className="space-y-3">
						{Object.entries(unplacedGroups).map(([deviceKey, channelMap]) => {
							const visibleChannels = Object.entries(channelMap).filter(
								([, sensors]) => channelMatchesSearch(sensors, searchQuery),
							);
							if (visibleChannels.length === 0) return null;
							const repSensor = visibleChannels[0]?.[1]?.[0];
							return (
								<div key={deviceKey}>
									<div className="px-1 mb-1">
										<p className="text-xs font-semibold text-amber-800">
											{deviceLabel(visibleChannels[0]?.[1] ?? [])}
										</p>
										{repSensor?.manufacturer && (
											<p className="text-xs text-amber-600">
												{repSensor.manufacturer}
											</p>
										)}
										{repSensor?.deviceFriendlyName && (
											<p className="text-xs text-amber-500">{deviceKey}</p>
										)}
									</div>
									{visibleChannels.map(([channelKey, channelSensors]) => {
										const unplacedRep = channelSensors[0];
										const unplacedChNum = unplacedRep?.channelNumber;
										const unplacedIsMulti = MULTI_CHANNEL_DEVICES.has(
											unplacedRep?.deviceFriendlyName ?? "",
										);
										const unplacedLabel = channelLabel(channelSensors);
										const unplacedShowChNum =
											!unplacedIsMulti &&
											unplacedChNum != null &&
											unplacedLabel !== `Channel ${unplacedChNum}`;
										return (
											<li
												key={channelKey}
												className="flex items-center justify-between px-2 py-1.5 rounded bg-amber-50 border border-amber-200 cursor-grab mb-1 list-none"
												draggable={canEdit}
												onDragStart={(e) =>
													e.dataTransfer.setData(
														"channel-sensor-ids",
														channelDragData(channelSensors),
													)
												}
											>
												<div>
													<p className="text-xs font-medium text-amber-800">
														{unplacedLabel}
														{unplacedShowChNum && (
															<span className="ml-1 text-amber-600 font-normal">
																· Channel {unplacedChNum}
															</span>
														)}
													</p>
													<p className="text-xs text-amber-600">
														{channelSensors
															.map((s) => humanizeSensorType(s.measureName))
															.join(" · ")}
													</p>
												</div>
												{canEdit && (
													<button
														type="button"
														onClick={() => onPlaceChannel(channelSensors)}
														className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 flex-shrink-0"
													>
														Place
													</button>
												)}
											</li>
										);
									})}
								</div>
							);
						})}
						{searchQuery &&
							Object.values(unplacedGroups).every((channelMap) =>
								Object.values(channelMap).every(
									(sensors) => !channelMatchesSearch(sensors, searchQuery),
								),
							) && (
								<p className="text-xs text-amber-500 px-1">
									No results for "{searchQuery}"
								</p>
							)}
					</div>
					</>
					)}
				</div>
			)}
		</div>
	);
}
